import { useSearchParamHelpers } from '../../_shared/react-hooks';
import { BrFilter, ProductFilterInput, SearchSortKeys } from 'src/generated/product-service-graphql';
import { NON_FILTER_QUERY_PARAMS } from '../../search-page/SearchPageUtils/constants';
import { useContext, useEffect, useMemo, useState } from 'react';
import { PlpContext } from '../PLPContext';
import { FilterOption } from '../../search-page/types';
import { brFiltersToFilterOptions } from '../../search-page/SearchPageUtils/BrFiltersToFilterOptions';
import { SORT_BY_KEY } from './sorting-utils';

export const useBrFilters = ({ brFilters, isPlp }: UseBrFiltersParams) => {
  const [appliedFilters, setAppliedFilters] = useState<ProductFilterInput[]>([]);
  const { searchParams, setSearchParams } = useSearchParamHelpers();
  const query = searchParams.get('q');
  const { togglePlpFiltersDrawer, setActiveFilterAccordion } = useContext(PlpContext);
  const selectedSortKey = searchParams.get(SORT_BY_KEY) as SearchSortKeys;

  const fixedParams = {
    ...(isPlp ? {} : { q: query || '' }),
    ...(selectedSortKey && { [SORT_BY_KEY]: selectedSortKey }),
  };

  const applyNewFilters = (newFilters: ProductFilterInput[]) => {
    const filters = newFilters
      .map((filter) => [filter.key, filter.values.join(',')])
      .reduce((acc, [key, values]) => ({ ...acc, [`${key}`]: values }), {});

    const urlSearchParams = new URLSearchParams({
      ...fixedParams,
      ...filters,
    });

    setSearchParams(urlSearchParams);
  };

  const handlePendingFilterChange = (filter: ProductFilterInput) => {
    const existingFilter = appliedFilters.find((f) => f.key === filter.key);

    if (existingFilter) {
      if (filter.values.length > 0) {
        applyNewFilters(appliedFilters.map((f) => (f.key === filter.key ? filter : f)));
      } else {
        applyNewFilters(appliedFilters.filter((f) => f.key !== filter.key));
      }
    } else {
      applyNewFilters([...appliedFilters, filter]);
    }
  };

  const findFilterLabelByValue = useMemo(() => {
    const filterList: FilterOption[] = brFiltersToFilterOptions(brFilters || []);
    return (value: string): string => {
      return filterList?.find((filterObj) => filterObj.filterValueInputValue === value)?.filterValueLabel || '';
    };
  }, [brFilters]);

  const availableFilters: FilterOption[] = useMemo(() => {
    return brFiltersToFilterOptions(brFilters || []);
  }, [brFilters]);

  const handleClearAllFilters = () => {
    const urlSearchParams = new URLSearchParams(fixedParams);
    setSearchParams(urlSearchParams);
  };

  const closeFiltersSidebar = () => {
    setActiveFilterAccordion('');
    togglePlpFiltersDrawer(false);
  };

  useEffect(() => {
    setAppliedFilters(
      Array.from(searchParams.entries())
        .filter(([key]) => !NON_FILTER_QUERY_PARAMS.includes(key))
        .filter(([key]) => (isPlp === true ? validFilterKeys.includes(key.toLowerCase()) : true))
        .map(([key, value]) => {
          return { key: key, values: value.split(',') };
        })
    );
  }, [searchParams, isPlp]);

  return {
    handlePendingFilterChange,
    handleClearAllFilters,
    closeFiltersSidebar,
    appliedFilters,
    findFilterLabelByValue,
    availableFilters,
  };
};

interface UseBrFiltersParams {
  brFilters: BrFilter[];
  isPlp?: boolean;
}

// ? [TEMP] for bugfix release, move to config
const validFilterKeys = [
  'Gender',
  'Product Type',
  'Size',
  'color',
  'fabric',
  'Support',
  'Length',
  'Shorts Inseam',
  "Men's Shorts Inseam",
  'Just Dropped',
  'activity',
].map((i) => i.toLowerCase());
